import { useId } from "react";
import type { ProyectoCatalogo } from "../types/reviewItem";

/**
 * Selector de proyecto con búsqueda, para el catálogo real (134 proyectos en
 * matriz_proyectos_catalogo, espejo exacto de los proyectos activos de SAP).
 *
 * Mismo patrón que CuentaCombobox, y por las mismas razones — decidido
 * comparando contra la otra alternativa del repo (el `<select>` simple de
 * Aprobador en ContaduriaPage):
 *  - Son 134 opciones, no 17. Un `<select>` nativo solo hace match por primera
 *    letra, así que encontrar "GENOMMA-TIO NACHO" obliga a scrollear a mano.
 *  - Los códigos son texto libre largo (nombres de empresa/marca), donde
 *    buscar es justamente lo que importa.
 *  - El navegador renderiza el popup FUERA del flujo del DOM. Determinante
 *    igual que en CuentaCombobox: `table.compact td` tiene `overflow: hidden`
 *    (global.css), así que un dropdown propio posicionado en absoluto queda
 *    RECORTADO dentro de la celda.
 *  - Cero dependencias nuevas.
 *
 * Salvedad conocida (heredada de CuentaCombobox): Chromium (Edge/Chrome, los
 * navegadores de uso real acá) filtra por código y por nombre; Firefox filtra
 * solo por el value (el código). Acá molesta poco, porque los códigos SON en
 * su mayoría el nombre de la empresa.
 *
 * A diferencia de la cuenta, el proyecto es **OPCIONAL**: vacío es un valor
 * perfectamente válido y no se marca en rojo. Lo que sí se marca (y el
 * llamador puede bloquear) es un texto que no corresponda a ningún proyecto
 * del catálogo — SAP valida `ProjectCode` contra sus propios Projects y
 * rechaza el documento entero con ODBC -2028 si no lo conoce.
 */
interface Props {
  valor: string;
  proyectos: ProyectoCatalogo[];
  onChange: (valor: string) => void;
  disabled?: boolean;
}

/** Exportada para que la página pueda bloquear la autorización de una fila con
 *  un proyecto inexistente. **Vacío cuenta como válido** — el dato es
 *  opcional; sin esto se bloquearía todo lo que nadie completó. */
export function proyectoOk(valor: string, proyectos: ProyectoCatalogo[]): boolean {
  if (valor.trim() === "") return true;
  return proyectos.some((p) => p.codigo_proyecto === valor);
}

export function ProyectoCombobox({ valor, proyectos, onChange, disabled }: Props) {
  // useId: puede haber decenas de filas en pantalla, cada input necesita su
  // propio list= (el id tiene que ser único en el documento).
  const listId = useId();
  const valido = proyectoOk(valor, proyectos);

  return (
    <>
      <input
        type="text"
        list={listId}
        className={valido ? "cuenta-input" : "cuenta-input cuenta-invalida"}
        value={valor}
        disabled={disabled}
        // [CAMBIADO 08/09/2026] Antes decía "Opcional — escribi para
        // buscar...", que describía CÓMO se usa pero no QUÉ es. Desde que las
        // 3 columnas condicionales se fusionaron en "Datos requeridos", el
        // placeholder es lo único que identifica el campo en esa celda —
        // mismo motivo por el que el de Carpeta pasó a decir "N. Carpeta".
        // Que es opcional y que se puede buscar escribiendo va en el title.
        placeholder="Cod. Proyecto"
        title={
          valido
            ? valor || "Código de proyecto (opcional) — escribí para buscar en el catálogo"
            : `"${valor}" no es un proyecto del catalogo`
        }
        onChange={(e) => onChange(e.target.value)}
      />
      <datalist id={listId}>
        {proyectos.map((p) => (
          <option key={p.codigo_proyecto} value={p.codigo_proyecto}>
            {p.nombre_proyecto ?? ""}
          </option>
        ))}
      </datalist>
    </>
  );
}
